/**
 * Taxonomy — the three-layer vocabulary a requisition describes itself with (Option B,
 * docs/TAXONOMY_PROPOSAL.md).
 *
 *   L-INDUSTRY   ~20 flat, NIC-aligned labels. Cheap on purpose: an industry flavours generated
 *                questions, adds guardrail overlay clauses (BFSI must never fish for
 *                client-confidential specifics) and segments analytics later. It NEVER touches
 *                scoring or calibration — that is the whole design, and the reason there are 20
 *                of them instead of 6 calibrated frameworks × 20 forks.
 *   L-FRAMEWORK  today's verticals (verticals.ts), untouched. Still the only objects that own
 *                competencies, weights, anchors and calibration keys.
 *   L-ROLE       role families (mandatory pick) and specialisations (optional multi-select) under
 *                each vertical, linked many-to-many. They steer question CONTENT; they carry no
 *                scoring machinery. Free text a recruiter types instead of picking is captured as
 *                a SUGGESTION for the owner's promotion queue AND used immediately for that one
 *                requisition's generation.
 *
 * NO VERSION MACHINERY here, deliberately. A vertical is versioned (editions, migration 0042)
 * because re-weighting it changes what a score MEANS. Renaming "Frontend engineer" to "UI
 * engineer" changes nothing a score depends on — questions are flavoured, scores are defended by
 * the vertical edition and the rubric snapshot. Versioning these rows would be provenance theatre.
 *
 * The role rows live in `role_families` / `role_specialisations` /
 * `role_family_specialisations` (migration 0045, seeded by 0046 from docs/taxonomy-seed.json);
 * industries are seeded inline in 0045 and MUST mirror INDUSTRIES below — taxonomy.test.ts parses
 * both migrations and asserts it. Everything in this file is PURE: no database, no network.
 */
import { z } from "zod";
import { requirementCheckDecisionSchema } from "./requirementCheck.js";
import { verticalKeySchema } from "./verticals.js";
/**
 * Free text at industry level lands on "other" and feeds the promotion queue — that is how the
 * list earns its 21st entry, rather than by guessing today.
 */
export const OTHER_INDUSTRY_KEY = "other";
/**
 * The 20 seeded industries, NIC-aligned, in display order. The SOURCE OF TRUTH for flavour and
 * guardrail overlays is this const (they are prompt engineering, reviewed in code); the DB rows
 * seeded by migration 0045 carry only the customer-facing fields and must match — the unit test
 * parses the migration and asserts key/name/description/aliases are identical.
 */
export const INDUSTRIES = [
    {
        key: "it_software_products",
        name: "IT & Software Products",
        description: "Companies whose product is the software itself — SaaS, platforms, developer tools and packaged software.",
        aliases: ["saas", "software product", "product company", "platform company", "developer tools", "product based"],
        flavour: "Assume product-led ways of working: release cycles, roadmaps, telemetry, and customers who use the software directly.",
    },
    {
        key: "it_services_gcc_bpo",
        name: "IT Services, GCC & BPO",
        description: "Technology and process work delivered to client organisations — consultancies, global capability centres and business-process outsourcing.",
        aliases: [
            "it services",
            "gcc",
            "global capability centre",
            "bpo",
            "kpo",
            "outsourcing",
            "managed services",
            "consultancy",
            "offshore development",
        ],
        flavour: "Assume client delivery: SLAs, statements of work, handovers across time zones, and standards set by someone else's engineering culture.",
        guardrails: [
            "Do not ask the candidate to name specific clients or reveal engagement terms, rates or deliverables covered by a client confidentiality agreement — let them describe work generically.",
        ],
    },
    {
        key: "bfsi",
        name: "BFSI",
        description: "Banking, financial services and insurance — lending, payments, markets, broking and insurance operations.",
        aliases: [
            "bank",
            "banking",
            "nbfc",
            "insurance",
            "fintech",
            "mutual fund",
            "broking",
            "payments",
            "microfinance",
            "financial services",
            "wealth management",
        ],
        flavour: "Assume a regulated context: maker-checker controls, audit trails, and RBI/SEBI/IRDAI vocabulary are natural here.",
        guardrails: [
            "Do not ask for client-confidential specifics — client names, account or portfolio details, deal terms, or anything covered by banking secrecy. Scenarios must stay hypothetical.",
            "Do not ask the candidate to share unpublished price-sensitive information from a current or former employer.",
        ],
    },
    {
        key: "healthcare_pharma",
        name: "Healthcare & Pharma",
        description: "Hospitals, clinics, diagnostics, pharmaceuticals, medical devices and the technology around them.",
        aliases: [
            "hospital",
            "clinic",
            "pharma",
            "pharmaceutical",
            "diagnostics",
            "medical devices",
            "healthtech",
            "life sciences",
        ],
        flavour: "Assume clinical adjacency: patient safety, documentation discipline and regulatory approval cycles shape the work.",
        guardrails: [
            "Do not ask for patient-identifiable details — cases must be discussed without names, identifiers or recognisable features.",
            "Do not ask about the candidate's own health, medical history or disability status.",
        ],
    },
    {
        key: "education_edtech",
        name: "Education & EdTech",
        description: "Schools, colleges, universities, coaching and training institutes, and education technology.",
        aliases: ["school", "college", "university", "edtech", "coaching institute", "training institute", "e learning"],
        flavour: "Assume learners and academic calendars: outcomes are measured in terms and cohorts, and parents or institutions are stakeholders alongside students.",
    },
    {
        key: "manufacturing_engineering",
        name: "Manufacturing & Engineering",
        description: "Factories, plants and heavy engineering — automotive, industrial equipment, components and OEMs.",
        aliases: [
            "factory",
            "plant",
            "automotive",
            "auto components",
            "heavy engineering",
            "industrial",
            "oem",
            "machinery",
        ],
        flavour: "Assume the shop floor: shifts, safety protocols, quality systems and physical throughput are the vocabulary of the work.",
    },
    {
        key: "fmcg",
        name: "FMCG",
        description: "Fast-moving consumer goods — brands, distribution and trade across food, beverage, personal and home care.",
        aliases: ["fmcg", "consumer goods", "cpg", "personal care", "packaged foods", "beverages", "general trade"],
        flavour: "Assume distribution reach: distributor and retailer relationships, trade schemes, and volume moved through channels.",
    },
    {
        key: "retail_ecommerce",
        name: "Retail & E-commerce",
        description: "Selling to consumers in stores and online — chains, marketplaces, D2C brands and omnichannel operations.",
        aliases: [
            "retail",
            "ecommerce",
            "e commerce",
            "online marketplace",
            "d2c",
            "direct to consumer",
            "supermarket",
            "department store",
        ],
        flavour: "Assume the customer is a consumer: footfall or traffic, conversion, seasonal peaks and returns are everyday concerns.",
    },
    {
        key: "logistics_qcommerce",
        name: "Logistics & Q-commerce",
        description: "Moving goods — supply chain, warehousing, freight, last-mile delivery and quick commerce.",
        aliases: [
            "logistics",
            "supply chain",
            "warehousing",
            "courier",
            "last mile",
            "quick commerce",
            "q commerce",
            "freight",
            "3pl",
            "delivery",
        ],
        flavour: "Assume time-bound physical operations: hubs, routes, fleet and the cost of every failed or late delivery.",
    },
    {
        key: "telecom_media",
        name: "Telecom & Media",
        description: "Connectivity and content — telecom operators, ISPs, broadcasting, OTT, publishing and entertainment.",
        aliases: ["telecom", "telco", "isp", "broadcasting", "media house", "ott", "publishing", "entertainment"],
        flavour: "Assume subscribers and audiences at scale: churn, content pipelines and network or platform reliability drive the work.",
    },
    {
        key: "hospitality_travel",
        name: "Hospitality & Travel",
        description: "Hotels, restaurants, airlines, travel and tourism — guest-facing service at its core.",
        aliases: ["hotel", "restaurant", "qsr", "travel agency", "airline", "tourism", "catering", "resort"],
        flavour: "Assume the guest experience: service recovery, peak-season pressure and standards delivered consistently shift after shift.",
    },
    {
        key: "real_estate_construction",
        name: "Real Estate & Construction",
        description: "Building and selling property — developers, contractors, EPC, infrastructure and proptech.",
        aliases: [
            "real estate",
            "construction",
            "infrastructure",
            "builder",
            "epc",
            "property",
            "proptech",
            "site engineer",
        ],
        flavour: "Assume projects and sites: milestones, contractors, approvals and safety on the ground set the rhythm of the work.",
    },
    {
        key: "energy_utilities",
        name: "Energy & Utilities",
        description: "Power, oil and gas, renewables and public utilities — generation, distribution and everything keeping them running.",
        aliases: ["power", "electricity", "oil and gas", "renewables", "solar", "wind", "utility", "water supply"],
        flavour: "Assume critical infrastructure: uptime, compliance and safety outrank speed, and outages have public consequences.",
    },
    {
        key: "agriculture_food",
        name: "Agriculture & Food",
        description: "Growing and processing food — farming, agritech, dairy, fisheries and food processing.",
        aliases: ["agriculture", "agritech", "agri", "farming", "dairy", "fisheries", "food processing", "seeds"],
        flavour: "Assume seasonality and the supply side: harvest cycles, perishability, mandis and rural distribution shape decisions.",
    },
    {
        key: "professional_services",
        name: "Professional Services",
        description: "Expertise sold by the engagement — accounting, audit, tax, law, consulting and advisory.",
        aliases: [
            "accounting",
            "audit",
            "law firm",
            "legal services",
            "tax",
            "consulting",
            "advisory",
            "chartered accountant",
        ],
        flavour: "Assume engagements and billable work: client deliverables, professional standards and deadlines set by statute or contract.",
        guardrails: [
            "Do not ask the candidate to reveal client identities or matter details protected by professional privilege or confidentiality — keep examples anonymised.",
        ],
    },
    {
        key: "staffing_hr",
        name: "Staffing & HR",
        description: "The talent business itself — staffing firms, recruitment agencies, HR services and payroll providers.",
        aliases: ["staffing", "recruitment agency", "hr services", "talent acquisition", "payroll services", "manpower"],
        flavour: "Assume two-sided delivery: clients with mandates on one side, candidates in process on the other, and fill metrics in between.",
    },
    {
        key: "govt_psu_defence",
        name: "Government, PSU & Defence",
        description: "Public administration, public-sector undertakings and defence — including bodies serving them.",
        aliases: ["government", "psu", "public sector", "defence", "municipal", "armed forces", "civic body"],
        flavour: "Assume public accountability: procedure, documentation and chain of approval matter as much as the outcome.",
        guardrails: [
            "Do not ask for classified, restricted or official-secrets information from current or past government or defence service — let the candidate describe their work in unclassified terms.",
            "Do not ask about the candidate's political opinions or party affiliations.",
        ],
    },
    {
        key: "ngo_social",
        name: "NGO & Social Sector",
        description: "Non-profits, foundations, social enterprises and CSR — mission-driven work and the operations behind it.",
        aliases: ["ngo", "non profit", "nonprofit", "charity", "foundation", "social enterprise", "csr", "development sector"],
        flavour: "Assume constrained resources and mission accountability: donors, beneficiaries and impact reporting frame the work.",
        guardrails: [
            "Do not ask about the candidate's own religious beliefs, political views or personal activism — commitment to the employer's mission is probed through their work, never their identity.",
        ],
    },
    {
        key: "textiles_apparel",
        name: "Textiles & Apparel",
        description: "Fibre to fashion — textile mills, garment manufacturing, apparel brands and leather goods.",
        aliases: ["textile", "garment", "apparel", "fashion", "spinning", "weaving", "handloom", "leather"],
        flavour: "Assume seasons and production runs: sampling, export orders, quality inspection and the fashion calendar drive priorities.",
    },
    {
        key: OTHER_INDUSTRY_KEY,
        name: "Other",
        description: "Anything the list above does not cover. What is typed here feeds the promotion queue — it is how the list grows.",
        aliases: [],
    },
];
export const INDUSTRY_KEYS = INDUSTRIES.map((i) => i.key);
export function industryByKey(key) {
    return INDUSTRIES.find((i) => i.key === key);
}
/**
 * Seniority as the understanding-check infers it from a JD. Coarse on purpose — it feeds prompt
 * context and the gap chips, not scoring, and a JD rarely supports anything finer.
 */
export const SENIORITY_LEVELS = ["entry", "junior", "mid", "senior", "lead", "executive"];
/**
 * Below this a guess is not shown as a pre-filled chip. The owner's rule: "if it can't be
 * matched it becomes mandatory" — a wrong pre-fill silently steers a whole interview, whereas a
 * required dropdown costs the recruiter two seconds.
 */
export const TAXONOMY_CONFIDENCE_FLOOR = 0.6;
export function isConfident(inferred, field) {
    const c = inferred.confidence[field];
    return typeof c === "number" && c >= TAXONOMY_CONFIDENCE_FLOOR;
}
/**
 * Which wizard fields must become REQUIRED manual picks because inference is absent or
 * under-confident. Verticals are excluded: the vertical picker is already mandatory today and
 * stays so regardless of inference.
 */
export function mandatoryPicks(inferred) {
    if (!inferred)
        return { industry: true, roleFamily: true };
    return {
        industry: !inferred.industryKey || !isConfident(inferred, "industry"),
        roleFamily: !inferred.roleFamilyKey || !isConfident(inferred, "roleFamily"),
    };
}
// ── Limits ────────────────────────────────────────────────────────────────────
/** Bounds mirrored by the CHECK constraints in migration 0045 — keep the two in step. */
export const TAXONOMY_LIMITS = {
    /** More than this stops narrowing the questions and starts diluting them. */
    maxSpecialisationsPerSelection: 12,
    minFreeTextLen: 2,
    /** A role NAME, not a paragraph — anything longer belongs in the JD, which we already have. */
    maxFreeTextLen: 160,
    maxAliasLen: 80,
    maxAliasesPerRow: 40,
};
// ── zod (wire schemas) ────────────────────────────────────────────────────────
/**
 * An industry / family / specialisation key on the wire. Slightly looser than vertical keys: a
 * leading DIGIT is allowed, because content vocabulary includes real terms like "5s_lean" (lean
 * manufacturing's 5S) — these keys live in jsonb and URLs, never as code identifiers.
 */
export const taxonomyKeySchema = z
    .string()
    .regex(/^[a-z0-9][a-z0-9_]{1,62}$/, "lower-case snake_case, starting with a letter or digit");
const confidence01 = z.number().min(0).max(1);
/** Free text is a suggestion-in-waiting, so it gets the suggestion's own caps at the source. */
const freeTextSchema = z
    .string()
    .trim()
    .min(TAXONOMY_LIMITS.minFreeTextLen)
    .max(TAXONOMY_LIMITS.maxFreeTextLen);
export const taxonomySelectionSchema = z.object({
    industryKey: taxonomyKeySchema,
    verticalKey: verticalKeySchema,
    roleFamilyKey: taxonomyKeySchema.optional(),
    specialisationKeys: z
        .array(taxonomyKeySchema)
        .max(TAXONOMY_LIMITS.maxSpecialisationsPerSelection)
        .default([]),
    freeText: freeTextSchema.optional(),
});
/** Where a piece of free text was typed — decides what the promotion queue can turn it into. */
export const SUGGESTION_LEVELS = ["industry", "role_family", "specialisation"];
export const SUGGESTION_STATUSES = ["new", "promoted", "dismissed"];
export const taxonomySuggestionSchema = z.object({
    rawText: freeTextSchema,
    level: z.enum(SUGGESTION_LEVELS),
    /** Context for role-level suggestions; industry-level ones have no vertical. */
    verticalKey: verticalKeySchema.optional(),
    requisitionId: z.string().uuid().optional(),
});
/**
 * The guided-intake model output, validated before anything renders. Everything optional and
 * defaulted: a partial inference is normal, an unparseable one means the card is silently absent
 * — never an error the recruiter sees.
 */
export const inferredTaxonomySchema = z.object({
    industryKey: taxonomyKeySchema.optional(),
    verticalKey: verticalKeySchema.optional(),
    roleFamilyKey: taxonomyKeySchema.optional(),
    specialisationKeys: z
        .array(taxonomyKeySchema)
        .max(TAXONOMY_LIMITS.maxSpecialisationsPerSelection)
        .default([]),
    seniority: z.enum(SENIORITY_LEVELS).optional(),
    confidence: z
        .object({
        industry: confidence01.optional(),
        vertical: confidence01.optional(),
        roleFamily: confidence01.optional(),
        specialisations: confidence01.optional(),
        seniority: confidence01.optional(),
    })
        .default({}),
});
/**
 * requisitions.taxonomy_enrichment — everything the understanding check gathered beyond the
 * selection itself. Passthrough on purpose: this is additive flywheel context read back for
 * generation and analytics, and a field a future build adds must not make old rows unreadable.
 */
export const taxonomyEnrichmentSchema = z
    .object({
    /** Gap-chip answers, keyed by chip id (e.g. "seniority", "must_haves"). */
    gapAnswers: z.record(z.string().max(600)).optional(),
    freeText: freeTextSchema.optional(),
    /** The one rethink advisory, if shown — and whether "Keep as-is" won. Both outcomes train. */
    rethink: z.object({ shown: z.string().max(600), keptAsIs: z.boolean() }).optional(),
    /**
     * The requirement check's outcome (requirementCheck.ts): which proposals the recruiter accepted or
     * declined, what they answered, what they added. The accepted text itself lives in the requirement
     * (the Clarifications block); this is the record of the choice, for the flywheel.
     */
    requirementCheck: requirementCheckDecisionSchema.optional(),
})
    .passthrough();
/** question_plan_snapshots.source — what the stored plan is: the machine's, or the human's edit. */
export const QUESTION_PLAN_SNAPSHOT_SOURCES = ["generated", "edited"];
// ── Validation helpers ────────────────────────────────────────────────────────
export function familyBelongsToVertical(families, verticalKey, familyKey) {
    return families.some((f) => f.verticalKey === verticalKey && f.key === familyKey);
}
/**
 * The specialisation keys that are NOT linked to the given family — [] means all belong. The
 * check runs against the m2m rather than the spec table because membership of the vertical is
 * not enough: "react" exists under software_engineering, but picking it under "engineering
 * manager" would target questions the family's competencies never probe.
 */
export function unlinkedSpecialisations(links, verticalKey, familyKey, specKeys) {
    const linked = new Set(links.filter((l) => l.verticalKey === verticalKey && l.familyKey === familyKey).map((l) => l.specKey));
    return specKeys.filter((k) => !linked.has(k));
}
/**
 * Readable validation for a requisition's taxonomy selection. Returns an EMPTY array when the
 * selection is storable; every string is a complete sentence safe to render in a banner. Same
 * contract as validateVertical: ALL problems at once, not the first parse failure.
 */
export function validateTaxonomySelection(selection, catalog) {
    const errors = [];
    const industries = catalog.industries ?? INDUSTRIES;
    const specKeys = selection.specialisationKeys ?? [];
    if (!industries.some((i) => i.key === selection.industryKey)) {
        errors.push(`Industry "${selection.industryKey}" is not in the catalogue.`);
    }
    if (selection.roleFamilyKey !== undefined) {
        if (!familyBelongsToVertical(catalog.families, selection.verticalKey, selection.roleFamilyKey)) {
            errors.push(`Role family "${selection.roleFamilyKey}" does not belong to the "${selection.verticalKey}" vertical.`);
        }
    }
    else if (specKeys.length > 0) {
        errors.push("Specialisations refine a role family — pick the family first.");
    }
    if (specKeys.length > TAXONOMY_LIMITS.maxSpecialisationsPerSelection) {
        errors.push(`A requisition may carry at most ${TAXONOMY_LIMITS.maxSpecialisationsPerSelection} specialisations; this one has ${specKeys.length}.`);
    }
    if (new Set(specKeys).size !== specKeys.length) {
        errors.push("The same specialisation is selected more than once.");
    }
    if (selection.roleFamilyKey !== undefined &&
        familyBelongsToVertical(catalog.families, selection.verticalKey, selection.roleFamilyKey)) {
        const unknown = unlinkedSpecialisations(catalog.links, selection.verticalKey, selection.roleFamilyKey, specKeys);
        if (unknown.length > 0) {
            errors.push(`Specialisation${unknown.length === 1 ? "" : "s"} ${unknown.map((k) => `"${k}"`).join(", ")} ${unknown.length === 1 ? "is" : "are"} not linked to the "${selection.roleFamilyKey}" role family.`);
        }
    }
    const freeText = selection.freeText?.trim() ?? "";
    if (selection.freeText !== undefined && freeText.length < TAXONOMY_LIMITS.minFreeTextLen) {
        errors.push("Free text needs at least a couple of characters to mean anything.");
    }
    if (freeText.length > TAXONOMY_LIMITS.maxFreeTextLen) {
        errors.push(`Free text is capped at ${TAXONOMY_LIMITS.maxFreeTextLen} characters — it names a role, it does not describe one.`);
    }
    return errors;
}
/**
 * Lower-case, letters and digits only, single spaces. Applied to BOTH sides so "Front-End
 * Developer" meets alias "front end developer" on equal terms.
 */
function normaliseForMatch(text) {
    return text
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, " ")
        .trim();
}
/**
 * Deterministic, dependency-free alias matcher — the pre-LLM pass over a JD. Whole-word phrase
 * containment only: "scala" must not fire inside "scalability", which is exactly the false
 * positive substring matching gives. Matches a row on its name, its humanised key or any alias,
 * keeps the LONGEST hit per row, and orders results most-specific-first (more words, then longer,
 * then key) so callers can take the head without re-ranking. Same inputs → same output, always —
 * this runs before any model and must never add noise of its own.
 */
/**
 * Role-abbreviation expansion — the pre-inference pass that fixes the "PM test case" (8 Aug 2026).
 *
 * "I need PM with 5 years experience for software development" sent the classifier to
 * software_engineering at p=0.28 noise and the Gemini fallback confidently to the same wrong
 * vertical, because "PM" meant nothing and "software development" dominated. Spelling the role out
 * ("Project Manager") classified correctly at p=0.31 — so the failure was the abbreviation, not
 * the taxonomy. Recruiters write in abbreviations; the models think in words. This bridges them.
 *
 * Rules, and why each exists:
 *  - WHOLE-WORD, case-sensitive on the short forms ("PM", not "pm…" inside a word): "rpm" and
 *    "capmap" must not expand.
 *  - A digit-or-time guard: "6 PM", "2pm-10pm shifts" are times, not roles. Any abbreviation
 *    directly preceded by a number (with optional space/colon) is left alone.
 *  - The expansion KEEPS the original in parentheses — "Project Manager (PM)" — so the models see
 *    both surfaces and nothing the recruiter typed is destroyed.
 *  - Deterministic and dependency-free, same input → same output, mirroring matchAliases above.
 */
const ROLE_ABBREVIATIONS = [
    [/\bPM\b/g, "Project Manager (PM)"],
    [/\bBA\b/g, "Business Analyst (BA)"],
    [/\bPO\b/g, "Product Owner (PO)"],
    [/\bSDE\b/g, "Software Development Engineer (SDE)"],
    [/\bSDET\b/g, "Software Development Engineer in Test (SDET)"],
    [/\bQA\b/g, "Quality Assurance (QA)"],
    [/\bSRE\b/g, "Site Reliability Engineer (SRE)"],
    [/\bRN\b/g, "Registered Nurse (RN)"],
    [/\bHRBP\b/g, "HR Business Partner (HRBP)"],
];
export function expandRoleAbbreviations(text) {
    let out = text;
    for (const [re, full] of ROLE_ABBREVIATIONS) {
        out = out.replace(re, (match, offset) => {
            // Time/number guard: "6 PM", "9:30 PM", "2PM". Look back past optional space/colon digits.
            const before = out.slice(Math.max(0, offset - 6), offset);
            if (/\d[\s:]*$/.test(before))
                return match;
            // Already expanded alongside? "Project Manager (PM)" must not become "... ((PM))".
            const after = out.slice(offset + match.length, offset + match.length + 2);
            if (before.endsWith("(") || after.startsWith(")"))
                return match;
            return full;
        });
    }
    return out;
}
export function matchAliases(text, rows) {
    const hay = ` ${normaliseForMatch(text)} `;
    if (hay.trim().length === 0)
        return [];
    const out = [];
    for (const row of rows) {
        // Insertion order fixes tie-breaks: name beats key beats aliases at equal length.
        const phrases = [];
        const push = (p) => {
            const n = normaliseForMatch(p);
            if (n.length >= TAXONOMY_LIMITS.minFreeTextLen && !phrases.includes(n))
                phrases.push(n);
        };
        push(row.name);
        push(row.key.replace(/_/g, " "));
        for (const a of row.aliases ?? [])
            push(a);
        let best = null;
        for (const p of phrases) {
            if (hay.includes(` ${p} `) && (best === null || p.length > best.length))
                best = p;
        }
        if (best !== null)
            out.push({ key: row.key, matched: best });
    }
    const words = (s) => s.split(" ").length;
    out.sort((a, b) => words(b.matched) - words(a.matched) ||
        b.matched.length - a.matched.length ||
        (a.key < b.key ? -1 : a.key > b.key ? 1 : 0));
    return out;
}
//# sourceMappingURL=taxonomy.js.map